<template>
	<PageTitleComponent />

	<div class="form-container">
		<form
			action="#"
			@submit.prevent="handlePasswordReset"
		>
			<h2>{{ isSetting ? 'Set' : 'Reset' }} Your Password</h2>

			<div
				class="reset-content"
				v-if="isReady"
			>
				<div class="fields">
					<div class="field">
						<input
							name="password"
							autocomplete="new-password"
							v-model="password"
							type="password"
							placeholder="New Password"
							required
							:pattern="passwordPattern"
							title="Password must be at least 8 characters long"
						/>
					</div>
					<div class="field">
						<input
							name="confirmPassword"
							autocomplete="new-password"
							v-model="confirmPassword"
							type="password"
							placeholder="Confirm New Password"
							required
						/>
					</div>
				</div>
				<div class="password-requirements">
					<small> Password must be at least 8 characters long </small>
				</div>
				<button
					class="btn btn-primary"
					type="submit"
					:disabled="!isFormValid || isSubmitting"
				>
					<span v-if="isSetting">
						{{ isSubmitting ? 'Setting...' : 'Set Password' }}
					</span>
					<span v-if="!isSetting">
						{{ isSubmitting ? 'Resetting...' : 'Reset Password' }}
					</span>
				</button>
			</div>

			<div
				class="error-container"
				v-if="isInvalidLink || hasError"
			>
				<div class="error">
					<div v-if="isInvalidLink">
						<p>There was an error with your reset link.</p>
						<p>Create a new link by entering your email address <router-link :to="{ name: 'forgot-password' }">here</router-link>.</p>
					</div>

					<div v-if="hasError">
						<p>There was an error processing your request.</p>
						<p>Create a new link by entering your email address <router-link :to="{ name: 'forgot-password' }">here</router-link>.</p>
						<p>Please <a href="mailto:info@realschools.com.au">contact support</a> if the issue persists.</p>
					</div>
				</div>
			</div>

			<div
				class="success-container"
				v-if="isAutoLoggingIn"
			>
				<div class="success">Your password has been successfully set and you are being logged in...You'll be redirected shortly.</div>
			</div>

			<div
				class="success-container"
				v-if="isSuccess"
			>
				<div class="success">
					Your password has been successfully set!
					<router-link
						:to="{
							name: 'login',
						}"
						>You can now log in with your new password.</router-link
					>
				</div>
			</div>

			<div
				class="login-link"
				v-if="!isSetting"
			>
				<small>
					<router-link
						:to="{
							name: 'login',
						}"
						>Back to Login</router-link
					>
				</small>
			</div>

			<LoadingComponent :loading="isSubmitting" />
		</form>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import PageTitleComponent from '@/components/shared/PageTitleComponent.vue';
import { useUser } from '@/stores/userStore';
import { mapActions } from 'pinia';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';

export default defineComponent({
	components: { PageTitleComponent, LoadingComponent },
	data() {
		return {
			password: '',
			confirmPassword: '',
			resetKey: '',
			resetEmail: '',
			passwordPattern: '.{8,}',
			isSetting: false,
			uiState: 'ready' as 'ready' | 'submitting' | 'invalid-link' | 'error' | 'auto-login' | 'success',
		};
	},
	computed: {
		isReady(): boolean {
			return this.uiState === 'ready';
		},
		isSubmitting(): boolean {
			return this.uiState === 'submitting';
		},
		isInvalidLink(): boolean {
			return this.uiState === 'invalid-link';
		},
		hasError(): boolean {
			return this.uiState === 'error';
		},
		isAutoLoggingIn(): boolean {
			return this.uiState === 'auto-login';
		},
		isSuccess(): boolean {
			return this.uiState === 'success';
		},
		isFormValid(): boolean {
			return this.isPasswordValid && this.passwordsMatch;
		},
		isPasswordValid(): boolean {
			const regex = new RegExp(this.passwordPattern);
			return regex.test(this.password);
		},
		passwordsMatch(): boolean {
			return this.password === this.confirmPassword && this.confirmPassword.length > 0;
		},
	},
	beforeMount() {
		this.checkResetKey();
		if (this.$route.query.isp) {
			this.isSetting = true;
		}
	},
	methods: {
		checkResetKey() {
			const urlParams = new URLSearchParams(window.location.search);
			const key = urlParams.get('key');

			if (!key) {
				this.uiState = 'invalid-link';
				return;
			}

			this.resetKey = key;
		},
		async handlePasswordReset() {
			if (!this.isFormValid || !this.resetKey || !this.isReady) {
				return;
			}

			this.uiState = 'submitting';

			const didReset = await this.resetPasswordFlow();
			if (!didReset) {
				return;
			}

			if (!this.isSetting) {
				this.uiState = 'success';
				return;
			}

			await this.attemptAutoLogin();
		},
		async resetPasswordFlow() {
			const res = await this.resetPassword(this.resetKey, this.password);

			if (!res.success) {
				this.uiState = 'error';
				return false;
			}

			this.resetEmail = res.data.email ?? '';

			return true;
		},
		async attemptAutoLogin() {
			if (!this.resetEmail) {
				this.uiState = 'error';
				return;
			}

			this.uiState = 'auto-login';
			await new Promise((resolve) => setTimeout(resolve, 3000));

			const loginResult = await this.login({
				username: this.resetEmail,
				password: this.password,
			});

			if (loginResult === false) {
				this.uiState = 'error';
				return;
			}

			this.uiState = 'success';
		},
		...mapActions(useUser, ['resetPassword', 'login']),
	},
});
</script>

<style scoped lang="scss">
.form-container {
	display: flex;
	flex: 1;
	justify-content: center;
	align-items: center;
	margin-bottom: 100px;
}

form {
	background-color: rgb(var(--rs-color-brand-navy));
	padding: var(--rs-layout-content-padding);
	width: clamp(200px, 400px, 100%);

	h2 {
		margin-bottom: 30px;
		color: white;
		font-size: 24px;
	}

	.fields {
		.field {
			margin-bottom: 20px;
		}
	}

	.password-requirements {
		margin-bottom: 20px;

		small {
			color: rgb(255 255 255 / 70%);
		}
	}

	.error-container {
		padding-bottom: 20px;

		.error {
			color: #ff6b6b;
		}

		a {
			color: white;
		}
	}

	.success-container {
		padding-bottom: 20px;

		.success {
			color: white;

			a {
				color: white;
			}
		}
	}

	.login-link {
		margin-top: 20px;

		a {
			color: white;
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
}
</style>
